import {
  ClientEmployeeFileUploadFieldsFragment,
  useGetClientEmployeeFileUploadsQuery,
} from "@graphql/client-employee-file-uploads/clientEmployeeFileUploads.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState, useCallback } from "react";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Table } from "@design-system/Table/Table";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import { ClientEmployeeFileUploadsFilterFormValues } from "./UI/ClientEmployeeFileUploadsTableFilters";
import {
  ClientEmployeeFileUploadsTableConfig,
  renderApplication,
  renderDatetime,
  renderEmployee,
  renderFileName,
  renderMimetype,
  renderUploadSize,
} from "./ClientEmployeeFileUploadsTableConfig";
import { ClientEmployeeFileUploadTableColumns } from "./UI/ClientEmployeeFileUploadsTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type Props = {
  clientId?: string;
  employeeId?: string;
  applicationId?: string;
  initialPageIndex?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
  filterValues: ClientEmployeeFileUploadsFilterFormValues;
};

export const ClientEmployeeFileUploadsTable = ({
  clientId,
  employeeId,
  applicationId,
  initialPageIndex = 0,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = PAGE_SIZE,
  filterValues,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedFileNameSearch = useDebounce(
    filterValues.fileNameSearch,
    SEARCH_DEBOUNCE_TIME
  );
  const debouncedMimetypeSearch = useDebounce(
    filterValues.mimetypeSearch,
    SEARCH_DEBOUNCE_TIME
  );

  const fileUploadsQuery = useGetClientEmployeeFileUploadsQuery({
    clientId,
    employeeId: filterValues.employeeId || employeeId,
    applicationId: filterValues.applicationId || applicationId,
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    fileName: debouncedFileNameSearch || undefined,
    mimetype: debouncedMimetypeSearch || undefined,
    datetime_Gte: filterValues.datetime_Gte,
    datetime_Lte: filterValues.datetime_Lte,
    sortBy: ConfigurableTableSystem.generateSortParameter(
      ClientEmployeeFileUploadsTableConfig.columns,
      sorting,
    ),
  });

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isLoading = fileUploadsQuery.isLoading;
  const isFetching = fileUploadsQuery.isFetching;
  const totalCount =
    fileUploadsQuery.data?.clientEmployeeFileUploads?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [
    debouncedFileNameSearch,
    debouncedMimetypeSearch,
    filterValues.datetime_Gte,
    filterValues.datetime_Lte,
    filterValues.employeeId,
    filterValues.applicationId,
    pageSize,
  ]);

  const data = useMemo(() => {
    return (
      fileUploadsQuery.data?.clientEmployeeFileUploads?.edges
        .map((edge) => edge?.node)
        .filter((node): node is ClientEmployeeFileUploadFieldsFragment => !!node) ?? []
    );
  }, [fileUploadsQuery.data]);

  const configuredColumns = useMemo(() => {
    const configWithRenderers = {
      ...ClientEmployeeFileUploadsTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientEmployeeFileUploadsTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (
                value: unknown,
                row: ClientEmployeeFileUploadFieldsFragment,
              ) => {
                const columnKey = key as ClientEmployeeFileUploadTableColumns;

                switch (columnKey) {
                  case ClientEmployeeFileUploadTableColumns.fileName:
                    return renderFileName(value);
                  case ClientEmployeeFileUploadTableColumns.application:
                    return renderApplication(row);
                  case ClientEmployeeFileUploadTableColumns.employee:
                    return renderEmployee(row);
                  case ClientEmployeeFileUploadTableColumns.mimetype:
                    return renderMimetype(value);
                  case ClientEmployeeFileUploadTableColumns.uploadSize:
                    return renderUploadSize(value);
                  case ClientEmployeeFileUploadTableColumns.datetime:
                    return renderDatetime(value);
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof ClientEmployeeFileUploadsTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientEmployeeFileUploadTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table<ClientEmployeeFileUploadFieldsFragment, string>
      columns={configuredColumns as ColumnDef<ClientEmployeeFileUploadFieldsFragment, string>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isLoading}
      isFetching={isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
      showFooter={showFooter}
      showHeader={showHeader}
      showPageSizeDropdown={showPageSizeDropdown}
      columnVisibility={columnSettings}
    />
  );
};